import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Container,
  Toolbar,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  AlertTitle,
  Snackbar,
  Breadcrumbs,
  Link,
  Paper,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Badge,
  LinearProgress,
  Tooltip,
  Collapse,
  CardMedia,
  Stack,
  ButtonGroup,
  FormControl,
  Select,
  InputLabel,
  BottomNavigation,
  BottomNavigationAction,
  AppBar,
  Drawer,
  useMediaQuery
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Notes as NotesIcon,
  UploadFile as UploadFileIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  PlaylistAddCheck as ChecklistIcon,
  CloudUpload as CloudUploadIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Image as ImageIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  AddTask as AddTaskIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilePresent as FilePresentIcon,
  Launch as LaunchIcon,
  PlayArrow as PlayArrowIcon,
  Fullscreen as FullscreenIcon,
  Info as InfoIcon,
  Share as ShareIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  ContentCopy as ContentCopyIcon,
  Public as PublicIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon,
  Chat as ChatIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Folder as FolderIcon
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useTheme } from '@mui/material/styles';
import { globalStyles } from "../../Utils/Styles";
import API from "../../API";
import useAuth from "../../Hooks/use-auth";
import routes from "../../Config/routes";
import OrganizerChatModal from "../../Components/ChatComponents/OrganizerChatModal";

const OrganizerDetailPage = ({ initialTab = 0 }) => {
  const { organizerId } = useParams();
  const navigate = useNavigate();
  const { getCurrrentUser } = useAuth();
  const user = getCurrrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Main State
  const [organizer, setOrganizer] = useState(null);
  const [allFiles, setAllFiles] = useState([]);
  const [organizerTypes, setOrganizerTypes] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [completionStats, setCompletionStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  // View State
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [fileFilter, setFileFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  
  // Mobile specific states
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(!isMobile);
  
  // Chat State
  const [chatModalOpen, setChatModalOpen] = useState(false);
  
  // Dialog states
  const [editOrganizerDialogOpen, setEditOrganizerDialogOpen] = useState(false);
  const [addFilesDialogOpen, setAddFilesDialogOpen] = useState(false);
  const [editNotesDialogOpen, setEditNotesDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [filePreviewDialog, setFilePreviewDialog] = useState({ open: false, file: null });
  const [fulfillDialogOpen, setFulfillDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareMode, setShareMode] = useState('users');
  const [viewSharesDialogOpen, setViewSharesDialogOpen] = useState(false);
  
  // Form states
  const [organizerForm, setOrganizerForm] = useState({
    name: '',
    description: '',
    organizer_type: 'general'
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editingNotes, setEditingNotes] = useState({
    fileId: null,
    notes: '',
    tags: []
  });
  
  // Checklist states
  const [newChecklistItem, setNewChecklistItem] = useState({
    title: '',
    description: '',
    required: true,
    expected_file_types: [],
    notes: ''
  });
  const [editingChecklistItem, setEditingChecklistItem] = useState(null);
  const [selectedChecklistItem, setSelectedChecklistItem] = useState(null);
  const [uploadMode, setUploadMode] = useState('select');
  
  // Upload state
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  // Sharing states
  const [shareForm, setShareForm] = useState({
    emails: [],
    permission_level: 'read',
    message: '',
    includePublicLink: false
  });
  const [shareEmailInput, setShareEmailInput] = useState('');
  const [publicLinkInfo, setPublicLinkInfo] = useState(null);
  const [sharingLoading, setSharingLoading] = useState(false);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuFile, setMenuFile] = useState(null);
  const [checklistMenuEl, setChecklistMenuEl] = useState(null);
  const [checklistMenuItem, setChecklistMenuItem] = useState(null);
  
  // Notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const fileTypeOptions = [
    'pdf', 'doc', 'docx', 'xlsx', 'xls', 'jpg', 'jpeg', 'png', 'gif', 'txt', 'csv'
  ];

  // Chat conversation query
  const { data: conversationData } = useQuery({
    queryKey: ['organizer-conversation', organizerId],
    queryFn: () => API.files.organizers.getConversation(organizerId),
    enabled: !!organizerId && !!organizer,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (user && organizerId) {
      loadData();
    }
  }, [user, organizerId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [organizerRes, allFilesRes, typesRes, checklistRes] = await Promise.all([
        API.files.organizers.get(organizerId),
        API.files.fetch(),
        API.files.organizers.getTypes(),
        API.files.organizers.getChecklist(organizerId, { include_files: true })
      ]);
      
      setOrganizer(organizerRes);
      setAllFiles(allFilesRes);
      setOrganizerTypes(typesRes);
      setChecklistItems(checklistRes.checklist_items || []);
      setCompletionStats(checklistRes.completion_stats || {});
      
      setOrganizerForm({
        name: organizerRes.name,
        description: organizerRes.description || '',
        organizer_type: organizerRes.organizer_type
      });
    } catch (error) {
      console.error("Error loading data:", error);
      showSnackbar("Error loading organizer", "error");
    }
    setLoading(false);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // File type detection helpers
  const isImageFile = (filename) => {
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];
    return imageExtensions.some((ext) => filename?.toLowerCase().endsWith(ext));
  };

  const isPdfFile = (filename) => {
    return filename?.toLowerCase().endsWith('.pdf');
  };

  const getFileIcon = (file) => {
    if (file.type === 'contract') return <AssignmentIcon color="primary" />;
    if (isImageFile(file.filename)) return <ImageIcon color="secondary" />;
    if (isPdfFile(file.filename)) return <PictureAsPdfIcon color="error" />;
    return <DescriptionIcon />;
  };

  const getCustomFileType = (file) => {
    if (file.type?.startsWith('image/')) return 'image';
    if (file.type === 'application/pdf') return 'pdf';
    if (file.type?.includes('document') || file.type?.includes('word')) return 'document';
    if (file.type?.includes('spreadsheet') || file.type?.includes('excel')) return 'spreadsheet';
    return 'misc';
  };

  // Public Link Management Functions
  const handleCreatePublicLink = async () => {
    setSharingLoading(true);
    try {
      const response = await API.files.organizers.createPublicLink(organizerId);
      setOrganizer(prev => ({
        ...prev,
        is_public: true,
        public_access_token: response.token
      }));
      setPublicLinkInfo(response);
      showSnackbar('Public link created successfully', 'success');
      loadData();
    } catch (error) {
      console.error('Error creating public link:', error);
      showSnackbar('Error creating public link', 'error');
    }
    setSharingLoading(false);
  };

  const handleRevokePublicLink = async () => {
    if (window.confirm('Are you sure you want to revoke public access? The current link will no longer work.')) {
      setSharingLoading(true);
      try {
        await API.files.organizers.revokePublicLink(organizerId);
        setPublicLinkInfo(null);
        showSnackbar('Public access revoked successfully', 'success');
        loadData();
      } catch (error) {
        console.error('Error revoking public link:', error);
        showSnackbar('Error revoking public link', 'error');
      }
      setSharingLoading(false);
    }
  };

  const handleCopyPublicLink = () => {
    let publicUrl = null;
    if (organizer.is_public && organizer.public_access_token) {
      publicUrl = `${window.location.origin}/organizer/${organizerId}?token=${organizer.public_access_token}`;
    } else if (publicLinkInfo?.public_url) {
      publicUrl = publicLinkInfo.public_url;
    } else if (publicLinkInfo?.token) {
      publicUrl = `${window.location.origin}/organizer/${organizerId}?token=${publicLinkInfo.token}`;
    }
    
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      showSnackbar('Public link copied to clipboard', 'success');
    } else {
      showSnackbar('No public link available', 'error');
    }
  };

  // User Sharing Functions
  const handleAddEmail = () => {
    const email = shareEmailInput.trim().toLowerCase();
    if (email && !shareForm.emails.includes(email)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        setShareForm(prev => ({
          ...prev,
          emails: [...prev.emails, email]
        }));
        setShareEmailInput('');
      } else {
        showSnackbar('Please enter a valid email address', 'error');
      }
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setShareForm(prev => ({
      ...prev,
      emails: prev.emails.filter(email => email !== emailToRemove)
    }));
  };

  const handleShareSubmit = async () => {
    if (shareForm.emails.length === 0) {
      showSnackbar('Please add at least one email address', 'error');
      return;
    }

    setSharingLoading(true);
    try {
      let shareData = { ...shareForm };

      if (shareForm.includePublicLink) {
        let publicUrl;
        if (organizer.is_public) {
          publicUrl = `${window.location.origin}/organizer/${organizerId}?token=${organizer.public_access_token}`;
        } else {
          const publicLinkResponse = await API.files.organizers.createPublicLink(organizerId);
          publicUrl = publicLinkResponse.public_url;
        }
        shareData.message = shareData.message + 
          `\n\nPublic Access Link: ${publicUrl}\n(No login required - anyone with this link can view and upload files)`;
      }

      const response = await API.files.organizers.shareOrganizer(organizerId, shareData);
      const successCount = response.total_shared || 0;
      showSnackbar(`Successfully shared with ${successCount} user${successCount !== 1 ? 's' : ''}`, 'success');
      
      setShareDialogOpen(false);
      setShareForm({ emails: [], permission_level: 'read', message: '', includePublicLink: false });
      setShareEmailInput('');
      loadData();
    } catch (error) {
      console.error('Error sharing organizer:', error);
      showSnackbar('Error sharing organizer', 'error');
    }
    setSharingLoading(false);
  };

  const handleViewShares = async () => {
    try {
      const shares = await API.files.organizers.getOrganizerShares(organizerId);
      setOrganizer(prev => ({ ...prev, shares: shares.shares }));
      setViewSharesDialogOpen(true);
    } catch (error) {
      console.error('Error fetching shares:', error);
      showSnackbar('Error fetching shares', 'error');
    }
  };

  const handleRemoveShare = async (shareId) => {
    if (window.confirm('Are you sure you want to remove this share?')) {
      try {
        await API.files.organizers.removeOrganizerShare(organizerId, shareId);
        showSnackbar('Share removed successfully', 'success');
        handleViewShares();
      } catch (error) {
        console.error('Error removing share:', error);
        showSnackbar('Error removing share', 'error');
      }
    }
  };

  // Enhanced file preview handler
  const handleFilePreview = (file) => {
    setFilePreviewDialog({ open: true, file });
  };

  const handleViewFile = (fileId) => {
    navigate(`/files/${fileId}`);
  };

  // File upload handlers
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    uploadFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    uploadFiles(files);
  };

  // Upload files function
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const fileData = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(file);
        });

        fileData.push({
          name: file.name,
          content: base64,
          type: file.type,
          file_type: getCustomFileType(file)
        });
        
        setUploadProgress(Math.round(((i + 1) / files.length) * 50));
      }

      const uploadResponse = await API.files.organizers.uploadToOrganizer(organizerId, {
        files: fileData
      });

      showSnackbar(`Successfully uploaded ${uploadResponse.total_uploaded} file(s)`, "success");
      
      if (uploadResponse.errors && uploadResponse.errors.length > 0) {
        console.warn('Some files had errors:', uploadResponse.errors);
        showSnackbar(`${uploadResponse.errors.length} files had errors`, "warning");
      }
      
      loadData();
      
    } catch (error) {
      console.error("Error uploading files:", error);
      const errorMessage = error?.response?.data?.error || error.message || "Error uploading files";
      showSnackbar(errorMessage, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadDialogOpen(false);
    }
  };

  // Checklist functions
  const handleCreateChecklistItem = async () => {
    if (!newChecklistItem.title.trim()) {
      showSnackbar('Title is required', 'error');
      return;
    }

    try {
      await API.files.organizers.createChecklistItem(organizerId, {
        title: newChecklistItem.title.trim(),
        description: newChecklistItem.description.trim() || null,
        required: newChecklistItem.required,
        expected_file_types: newChecklistItem.expected_file_types || [],
        notes: newChecklistItem.notes.trim() || null
      });

      setChecklistDialogOpen(false);
      setNewChecklistItem({ title: '', description: '', required: true, expected_file_types: [], notes: '' });
      loadData();
      showSnackbar('Checklist item created', 'success');
    } catch (error) {
      console.error('Error creating checklist item:', error);
      showSnackbar('Error creating checklist item', 'error');
    }
  };

  const handleUpdateChecklistItem = async () => {
    try {
      await API.files.organizers.updateChecklistItem(organizerId, editingChecklistItem.id, editingChecklistItem);
      setChecklistDialogOpen(false);
      setEditingChecklistItem(null);
      loadData();
      showSnackbar('Checklist item updated', 'success');
    } catch (error) {
      console.error('Error updating checklist item:', error);
      showSnackbar('Error updating checklist item', 'error');
    }
  };

  const handleFulfillChecklistItem = async (itemId, fileId) => {
    try {
      await API.files.organizers.fulfillChecklistItem(organizerId, itemId, { file_id: fileId });
      setFulfillDialogOpen(false);
      setSelectedChecklistItem(null);
      loadData();
      showSnackbar('Checklist item fulfilled', 'success');
    } catch (error) {
      console.error('Error fulfilling checklist item:', error);
      showSnackbar('Error fulfilling checklist item', 'error');
    }
  };

  const handleUnfulfillChecklistItem = async (itemId) => {
    try {
      await API.files.organizers.unfulfillChecklistItem(organizerId, itemId);
      loadData();
      showSnackbar('Checklist item marked incomplete', 'success');
    } catch (error) {
      console.error('Error marking item incomplete:', error);
      showSnackbar('Error marking item incomplete', 'error');
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this checklist item?')) {
      try {
        await API.files.organizers.deleteChecklistItem(organizerId, itemId);
        loadData();
        showSnackbar('Checklist item deleted', 'success');
      } catch (error) {
        console.error('Error deleting checklist item:', error);
        showSnackbar('Error deleting checklist item', 'error');
      }
    }
  };

  // Upload to checklist item
  const handleChecklistFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedChecklistItem) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(file);
      });

      setUploadProgress(50);

      await API.files.organizers.uploadToChecklistItem(organizerId, selectedChecklistItem.id, {
        file: {
          name: file.name,
          content: base64,
          type: file.type
        }
      });

      setUploadProgress(100);
      setFulfillDialogOpen(false);
      setSelectedChecklistItem(null);
      loadData();
      showSnackbar('File uploaded and checklist item fulfilled', 'success');
    } catch (error) {
      console.error('Error uploading file:', error);
      showSnackbar('Error uploading file', 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Other existing handlers
  const handleUpdateOrganizer = async () => {
    try {
      await API.files.organizers.update(organizerId, organizerForm);
      showSnackbar("Organizer updated successfully", "success");
      setEditOrganizerDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error updating organizer:", error);
      showSnackbar("Error updating organizer", "error");
    }
  };

  const handleDeleteOrganizer = async () => {
    try {
      await API.files.organizers.delete(organizerId);
      showSnackbar("Organizer deleted successfully", "success");
      setConfirmDeleteDialogOpen(false);
      navigate(routes.organizers || '/organizers');
    } catch (error) {
      console.error("Error deleting organizer:", error);
      showSnackbar("Error deleting organizer", "error");
    }
  };

  const handleAddFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    try {
      const fileIds = selectedFiles.map(file => file.id);
      await API.files.organizers.addExistingFiles(organizerId, { file_ids: fileIds });
      
      showSnackbar(`Added ${selectedFiles.length} file(s) to organizer`, "success");
      setAddFilesDialogOpen(false);
      setSelectedFiles([]);
      loadData();
    } catch (error) {
      console.error("Error adding files:", error);
      showSnackbar("Error adding files to organizer", "error");
    }
  };

  const handleRemoveFile = async (fileId) => {
    try {
      await API.files.organizers.removeFile(organizerId, fileId);
      showSnackbar("File removed from organizer", "success");
      loadData();
    } catch (error) {
      console.error("Error removing file:", error);
      showSnackbar("Error removing file", "error");
    }
  };

  const handleReorderFiles = async (result) => {
    if (!result.destination) return;

    const files = Array.from(organizer.files);
    const [reorderedFile] = files.splice(result.source.index, 1);
    files.splice(result.destination.index, 0, reorderedFile);

    const filePositions = files.map((file, index) => ({
      file_id: file.id,
      position: index + 1
    }));

    try {
      await API.files.organizers.reorderFiles(organizerId, { file_positions: filePositions });
      loadData();
    } catch (error) {
      console.error("Error reordering files:", error);
      showSnackbar("Error reordering files", "error");
    }
  };

  const handleUpdateFileNotes = async () => {
    try {
      await API.files.organizers.updateFileNotes(
        organizerId,
        editingNotes.fileId,
        {
          notes: editingNotes.notes,
          tags: editingNotes.tags
        }
      );
      
      showSnackbar("File notes updated", "success");
      setEditNotesDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error updating file notes:", error);
      showSnackbar("Error updating file notes", "error");
    }
  };

  const handleMenuOpen = (event, file) => {
    setAnchorEl(event.currentTarget);
    setMenuFile(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuFile(null);
  };

  const handleChecklistMenuOpen = (event, item) => {
    event.stopPropagation();
    setChecklistMenuEl(event.currentTarget);
    setChecklistMenuItem(item);
  };

  const handleChecklistMenuClose = () => {
    setChecklistMenuEl(null);
    setChecklistMenuItem(null);
  };

  const openEditNotesDialog = (file) => {
    setEditingNotes({
      fileId: file.id,
      notes: file.notes || '',
      tags: file.tags || []
    });
    setEditNotesDialogOpen(true);
    handleMenuClose();
  };

  const handleDownloadFile = (file) => {
    const link = document.createElement("a");
    link.href = file.file_url;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleMenuClose();
  };

  // Toggle expanded state for checklist items
  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  // Filter functions
  const getFilteredFiles = () => {
    if (!organizer?.files) return [];
    
    let filtered = organizer.files;
    
    if (searchQuery) {
      filtered = filtered.filter(file =>
        file.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (file.notes && file.notes.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    if (fileFilter !== 'all') {
      filtered = filtered.filter(file => {
        switch (fileFilter) {
          case 'images':
            return isImageFile(file.filename);
          case 'pdfs':
            return isPdfFile(file.filename);
          case 'docs':
            return !isImageFile(file.filename) && !isPdfFile(file.filename);
          default:
            return true;
        }
      });
    }
    
    return filtered;
  };

  const getFilteredChecklistItems = () => {
    let filtered = checklistItems;
    
    if (!showCompleted) {
      filtered = filtered.filter(item => !item.completed);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    return filtered;
  };

  const availableFiles = allFiles.filter(file => 
    !organizer?.files?.some(orgFile => orgFile.id === file.id)
  );

  const filteredFiles = getFilteredFiles();
  const filteredChecklistItems = getFilteredChecklistItems();

  // Mobile drawer content
  const renderMobileDrawer = () => (
    <Drawer
      anchor="right"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
      sx={{ '& .MuiDrawer-paper': { width: 280, p: 2 } }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Actions</Typography>
        <IconButton onClick={() => setMobileDrawerOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Stack spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => {
            setEditOrganizerDialogOpen(true);
            setMobileDrawerOpen(false);
          }}
        >
          Edit Organizer
        </Button>
        
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ShareIcon />}
          onClick={() => {
            setShareDialogOpen(true);
            setMobileDrawerOpen(false);
          }}
        >
          Share
        </Button>
        
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ChatIcon />}
          onClick={() => {
            setChatModalOpen(true);
            setMobileDrawerOpen(false);
          }}
        >
          Team Chat
        </Button>
        
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setAddFilesDialogOpen(true);
            setMobileDrawerOpen(false);
          }}
          disabled={availableFiles.length === 0}
        >
          Add Files
        </Button>
        
        <Divider sx={{ my: 2 }} />
        
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => {
            setConfirmDeleteDialogOpen(true);
            setMobileDrawerOpen(false);
          }}
        >
          Delete Organizer
        </Button>
      </Stack>
    </Drawer>
  );

  // Mobile summary card
  const renderSummaryCard = () => (
    <Card sx={{ mb: 2, borderLeft: '4px solid', borderColor: 'primary.main' }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                sx={{ fontWeight: "bold", fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
                {organizer.name}
              </Typography>
              {isMobile && (
                <IconButton 
                  size="small"
                  onClick={() => setMobileDrawerOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip label={organizer.organizer_type} color="primary" variant="outlined" size="small" />
              {organizer.is_public && (
                <Chip 
                  label="Public" 
                  color="primary"
                  variant="outlined"
                  icon={<PublicIcon />}
                  size="small"
                  sx={{ color: 'green', borderColor: 'green' }}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {organizer.files?.length || 0} files
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {checklistItems.length} tasks
              </Typography>
              {conversationData?.conversation_id && (
                <Chip 
                  label="Chat Active" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  icon={<ChatIcon />}
                />
              )}
            </Box>

            {checklistItems.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    Progress
                  </Typography>
                  <Typography variant="body2">
                    {completionStats.completed_items || 0}/{completionStats.total_items || 0}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={completionStats.completion_percentage || 0} 
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}
          </Box>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
              <ButtonGroup orientation="vertical" size="small">
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditOrganizerDialogOpen(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={() => setShareDialogOpen(true)}
                >
                  Share
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ChatIcon />}
                  onClick={() => setChatModalOpen(true)}
                >
                  Chat
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                >
                  Upload
                </Button>
              </ButtonGroup>
            </Box>
          )}
        </Box>
        
        {organizer.description && (
          <Collapse in={summaryExpanded}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {organizer.description}
            </Typography>
          </Collapse>
        )}
      </CardContent>
      
      {organizer.description && isMobile && (
        <CardActions sx={{ pt: 0 }}>
          <Button 
            size="small" 
            onClick={() => setSummaryExpanded(!summaryExpanded)}
            endIcon={summaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {summaryExpanded ? 'Less' : 'More'}
          </Button>
        </CardActions>
      )}
    </Card>
  );

  // Mobile search and filter bar
  const renderSearchFilter = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={`Search ${activeTab === 0 ? 'everything' : activeTab === 1 ? 'files' : activeTab === 2 ? 'checklist' : 'sharing'}...`}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
        }}
        sx={{ mb: 2 }}
      />
      
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={fileFilter}
              onChange={(e) => setFileFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="all">All Files</MenuItem>
              <MenuItem value="images">Images</MenuItem>
              <MenuItem value="pdfs">PDFs</MenuItem>
              <MenuItem value="docs">Documents</MenuItem>
            </Select>
          </FormControl>
          
          {!isSmall && (
            <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
              <IconButton
                size="small"
                onClick={() => setViewMode('grid')}
                color={viewMode === 'grid' ? 'primary' : 'default'}
              >
                <GridViewIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setViewMode('list')}
                color={viewMode === 'list' ? 'primary' : 'default'}
              >
                <ListViewIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
      
      {activeTab === 2 && (
        <FormControlLabel
          control={
            <Switch
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              size="small"
            />
          }
          label="Show Completed"
        />
      )}
    </Paper>
  );

  // Tab content renderers
  const renderOverviewTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileIcon />
              Files ({organizer.files?.length || 0})
            </Typography>
            {organizer.files?.slice(0, 3).map((file) => (
              <Box key={file.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                {getFileIcon(file)}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" noWrap>{file.filename}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(file.added_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => handleFilePreview(file)}>
                  <VisibilityIcon />
                </IconButton>
              </Box>
            ))}
            {organizer.files?.length > 3 && (
              <Button 
                variant="text" 
                onClick={() => setActiveTab(1)}
                size="small"
                sx={{ mt: 1 }}
              >
                View all {organizer.files.length} files
              </Button>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChecklistIcon />
              Checklist ({checklistItems.length})
            </Typography>
            {checklistItems.slice(0, 3).map((item) => (
              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                {item.completed ? (
                  <CheckCircleIcon sx={{ color: 'green' }} />
                ) : (
                  <RadioButtonUncheckedIcon color="disabled" />
                )}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" noWrap>{item.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.required ? 'Required' : 'Optional'}
                  </Typography>
                </Box>
              </Box>
            ))}
            {checklistItems.length > 3 && (
              <Button 
                variant="text" 
                onClick={() => setActiveTab(2)}
                size="small"
                sx={{ mt: 1 }}
              >
                View all {checklistItems.length} items
              </Button>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilesTab = () => (
    <Box>
      {!organizer.files || organizer.files.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No files yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload files to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Files
          </Button>
        </Paper>
      ) : filteredFiles.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No files match your search
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={isMobile ? 1 : 2}>
          {filteredFiles.map((file) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={file.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': { transform: isMobile ? 'none' : 'translateY(-2px)', boxShadow: 2 }
                }}
                onClick={() => handleFilePreview(file)}
              >
                <Box sx={{ 
                  position: 'relative', 
                  height: { xs: 80, sm: 120 }, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 1
                }}>
                  {isImageFile(file.filename) ? (
                    <img
                      src={file.file_url}
                      alt={file.filename}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      {getFileIcon(file)}
                    </Box>
                  )}
                  
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnchorEl(e.currentTarget);
                      setMenuFile(file);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
                
                <CardContent sx={{ pt: 1, pb: '8px !important', px: 1 }}>
                  <Typography 
                    variant="caption" 
                    noWrap 
                    sx={{ 
                      display: 'block',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }}
                  >
                    {file.filename}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {new Date(file.added_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const renderChecklistTab = () => (
    <Box>
      {checklistItems.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ChecklistIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No checklist items yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create checklist items to track tasks and files
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setChecklistDialogOpen(true)}
          >
            Add First Item
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {filteredChecklistItems.map((item) => (
            <Card 
              key={item.id}
              sx={{ 
                borderLeft: item.completed ? '4px solid #4caf50' : item.required ? '4px solid #ff9800' : '4px solid #e0e0e0',
                opacity: item.completed ? 0.95 : 1
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexGrow: 1 }}>
                    <Box sx={{ pt: 0.5 }}>
                      {item.completed ? (
                        <CheckCircleIcon sx={{ color: 'green' }} />
                      ) : (
                        <RadioButtonUncheckedIcon color="disabled" />
                      )}
                    </Box>
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant={isMobile ? "subtitle2" : "h6"}>
                          {item.title}
                        </Typography>
                        {item.required && (
                          <Chip 
                            label="Required" 
                            size="small"
                            sx={{ color: 'orange', borderColor: 'orange' }}
                            variant="outlined"
                          />
                        )}
                        {item.completed && (
                          <Chip 
                            label="Done" 
                            size="small"
                            icon={<CheckCircleIcon />}
                            sx={{ color: 'green', borderColor: 'green' }}
                            variant="outlined"
                          />
                        )}
                      </Box>
                      
                      {item.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {item.description}
                        </Typography>
                      )}
                      
                      {item.completed && item.file && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getFileIcon(item.file)}
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {item.file.filename}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Completed: {new Date(item.completed_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => handleFilePreview(item.file)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  
                  <IconButton 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setChecklistMenuEl(e.currentTarget);
                      setChecklistMenuItem(item);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
                
                {!item.completed && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => {
                        setSelectedChecklistItem(item);
                        setUploadMode('upload');
                        setFulfillDialogOpen(true);
                      }}
                    >
                      Upload
                    </Button>
                    {availableFiles.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AssignmentIcon />}
                        onClick={() => {
                          setSelectedChecklistItem(item);
                          setUploadMode('select');
                          setFulfillDialogOpen(true);
                        }}
                      >
                        Assign
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );

  const renderSharingTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PublicIcon />
              Public Access
            </Typography>
            
            {organizer.is_public ? (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Public Access Enabled</AlertTitle>
                  Anyone with the link can view and upload files.
                </Alert>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyPublicLink}
                    size="small"
                  >
                    Copy Link
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LinkOffIcon />}
                    onClick={handleRevokePublicLink}
                    size="small"
                    disabled={sharingLoading}
                  >
                    Revoke
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <PublicIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Public Link
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create a public link for easy sharing
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleCreatePublicLink}
                  disabled={sharingLoading}
                  size="small"
                >
                  {sharingLoading ? 'Creating...' : 'Create Link'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon />
                User Sharing
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  setShareMode('users');
                  setShareDialogOpen(true);
                }}
                size="small"
              >
                Share
              </Button>
            </Box>
            
            {organizer.shares && organizer.shares.length > 0 ? (
              <Typography variant="body2" color="text.secondary">
                Shared with {organizer.shares.length} user{organizer.shares.length !== 1 ? 's' : ''}
              </Typography>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  Not shared with users yet
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organizer) {
    return (
      <Box sx={{ backgroundColor: "#f5f5f5", width: "100%", color: "#000" }}>
        <Toolbar />
        <Container sx={{ ...globalStyles.wholeHeightMinusToolbar, pt: 6, pb: 4 }}>
          <Alert severity="error">Organizer not found</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#f5f5f5", width: "100%", color: "#000", pb: isMobile ? 8 : 4 }}>
      <Toolbar />
      <Container sx={{ ...globalStyles.wholeHeightMinusToolbar, pt: { xs: 2, sm: 4 }, pb: 2, px: { xs: 1, sm: 2, md: 3 } }}>
        
        {/* Breadcrumbs - Desktop only */}
        {!isMobile && (
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link 
              component="button" 
              variant="body2" 
              onClick={() => navigate('/files')}
              sx={{ textDecoration: 'none' }}
            >
              Your Files
            </Link>
            <Typography variant="body2" color="text.primary">
              {organizer.name}
            </Typography>
          </Breadcrumbs>
        )}

        {/* Mobile back button */}
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={() => navigate('/files')} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              Back to Files
            </Typography>
          </Box>
        )}

        {/* Summary Card */}
        {renderSummaryCard()}
{isMobile ? (
          <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }}>
            <BottomNavigation
              value={activeTab}
              onChange={(e, value) => setActiveTab(value)}
              showLabels
            >
              <BottomNavigationAction label="Overview" icon={<HomeIcon />} />
              <BottomNavigationAction 
                label="Files" 
                icon={<Badge badgeContent={filteredFiles.length} color="primary"><FileIcon /></Badge>} 
              />
              <BottomNavigationAction 
                label="Checklist" 
                icon={<Badge badgeContent={filteredChecklistItems.length} color="primary"><ChecklistIcon /></Badge>} 
              />
              <BottomNavigationAction label="Share" icon={<ShareIcon />} />
            </BottomNavigation>
          </Paper>
        ) : (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
              <Tab label="Overview" icon={<HomeIcon />} />
              <Tab label={`Files (${filteredFiles.length})`} icon={<FileIcon />} />
              <Tab label={`Checklist (${filteredChecklistItems.length})`} icon={<ChecklistIcon />} />
              <Tab label="Sharing" icon={<ShareIcon />} />
            </Tabs>
          </Box>
        )}

        {/* Search and Filter */}
        {renderSearchFilter()}

        {/* Navigation Tabs */}
    
        {/* Tab Content */}
        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && renderOverviewTab()}
          {activeTab === 1 && renderFilesTab()}
          {activeTab === 2 && renderChecklistTab()}
          {activeTab === 3 && renderSharingTab()}
        </Box>

        {/* Mobile FABs */}
        {isMobile && (
          <>
            <Fab
              color="primary"
              aria-label="upload"
              onClick={() => setUploadDialogOpen(true)}
              sx={{
                position: 'fixed',
                bottom: 80,
                right: 16,
                zIndex: 1000
              }}
            >
              <UploadFileIcon />
            </Fab>
            
            {activeTab === 2 && (
              <Fab
                color="secondary"
                aria-label="add checklist item"
                onClick={() => setChecklistDialogOpen(true)}
                sx={{
                  position: 'fixed',
                  bottom: 80,
                  right: 80,
                  zIndex: 1000
                }}
                size="medium"
              >
                <AddTaskIcon />
              </Fab>
            )}
          </>
        )}

        {/* Mobile drawer */}
        {isMobile && renderMobileDrawer()}

        {/* Enhanced Share Dialog */}
        <Dialog 
          open={shareDialogOpen} 
          onClose={() => setShareDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {isMobile && (
              <IconButton
                onClick={() => setShareDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShareIcon />
              Share "{organizer.name}"
            </Box>
          </DialogTitle>
          <DialogContent>
            <Tabs value={shareMode} onChange={(e, newValue) => setShareMode(newValue)} sx={{ mb: 2 }}>
              <Tab label="Share with Users" value="users" />
              <Tab label="Public Link" value="public" />
            </Tabs>

            {shareMode === 'users' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Share this organizer with specific users by entering their email addresses.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter email address"
                    value={shareEmailInput}
                    onChange={(e) => setShareEmailInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddEmail();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddEmail}
                    startIcon={<PersonAddIcon />}
                    disabled={!shareEmailInput.trim()}
                  >
                    Add
                  </Button>
                </Box>

                {shareForm.emails.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sharing with ({shareForm.emails.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {shareForm.emails.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          onDelete={() => handleRemoveEmail(email)}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Permission Level</InputLabel>
                  <Select
                    value={shareForm.permission_level}
                    onChange={(e) => setShareForm(prev => ({ ...prev, permission_level: e.target.value }))}
                    label="Permission Level"
                  >
                    <MenuItem value="read">Read Only</MenuItem>
                    <MenuItem value="write">Can Edit</MenuItem>
                    <MenuItem value="admin">Full Access</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={shareForm.includePublicLink || false}
                      onChange={(e) => setShareForm(prev => ({ ...prev, includePublicLink: e.target.checked }))}
                    />
                  }
                  label="Include public access link in email"
                />

                {shareForm.includePublicLink && (
                  <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                    A public access link will be created and included in the sharing email.
                  </Alert>
                )}

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Message (optional)"
                  placeholder="Add a personal message..."
                  value={shareForm.message}
                  onChange={(e) => setShareForm(prev => ({ ...prev, message: e.target.value }))}
                />
              </Box>
            )}

            {shareMode === 'public' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create a public link that anyone can use to view and upload files. No login required.
                </Typography>

                {organizer.is_public ? (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Public access is enabled for this organizer.
                    </Alert>
                    
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>Public Link:</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={`${window.location.origin}/organizer/${organizerId}?token=${organizer.public_access_token}`}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <IconButton onClick={handleCopyPublicLink}>
                              <ContentCopyIcon />
                            </IconButton>
                          )
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyPublicLink}
                        size="small"
                      >
                        Copy Link
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<LinkOffIcon />}
                        onClick={handleRevokePublicLink}
                        size="small"
                      >
                        Revoke Access
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <PublicIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      No Public Link Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Create a public link to allow anyone to view and upload files.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<LinkIcon />}
                      onClick={handleCreatePublicLink}
                      disabled={sharingLoading}
                    >
                      {sharingLoading ? 'Creating...' : 'Create Public Link'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          {!isMobile && (
            <DialogActions>
              <Button onClick={() => setShareDialogOpen(false)}>
                Close
              </Button>
              {shareMode === 'users' && (
                <Button
                  onClick={handleShareSubmit}
                  variant="contained"
                  disabled={shareForm.emails.length === 0 || sharingLoading}
                  startIcon={<ShareIcon />}
                >
                  {sharingLoading ? 'Sharing...' : `Share with ${shareForm.emails.length} user${shareForm.emails.length !== 1 ? 's' : ''}`}
                </Button>
              )}
            </DialogActions>
          )}
        </Dialog>

        {/* Upload Dialog */}
        <Dialog 
          open={uploadDialogOpen} 
          onClose={() => !uploading && setUploadDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {isMobile && (
              <IconButton
                onClick={() => setUploadDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            Upload Files
          </DialogTitle>
          <DialogContent>
            {uploading ? (
              <Box sx={{ py: 4 }}>
                <Typography gutterBottom>Uploading files...</Typography>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {uploadProgress}% complete
                </Typography>
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Files will be uploaded directly to this organizer.
                </Alert>
                <Box
                  sx={{
                    border: 2,
                    borderColor: dragActive ? 'primary.main' : 'divider',
                    borderStyle: 'dashed',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: dragActive ? 'action.hover' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-input').click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isMobile ? 'Tap to select files' : 'Drop files here or click to browse'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Files will be uploaded automatically
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          {!isMobile && (
            <DialogActions>
              <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Cancel'}
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* Edit Organizer Dialog */}
        <Dialog open={editOrganizerDialogOpen} onClose={() => setEditOrganizerDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>
            {isMobile && (
              <IconButton
                onClick={() => setEditOrganizerDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            Edit Organizer
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Organizer Name"
              fullWidth
              variant="outlined"
              value={organizerForm.name}
              onChange={(e) => setOrganizerForm({...organizerForm, name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={organizerForm.description}
              onChange={(e) => setOrganizerForm({...organizerForm, description: e.target.value})}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              options={organizerTypes}
              getOptionLabel={(option) => option.label}
              value={organizerTypes.find(type => type.value === organizerForm.organizer_type) || null}
              onChange={(event, newValue) => {
                setOrganizerForm({...organizerForm, organizer_type: newValue?.value || 'general'});
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organizer Type"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOrganizerDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleUpdateOrganizer} 
              variant="contained"
              disabled={!organizerForm.name}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Files Dialog */}
        <Dialog open={addFilesDialogOpen} onClose={() => setAddFilesDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle>
            {isMobile && (
              <IconButton
                onClick={() => setAddFilesDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            Add Files to {organizer.name}
          </DialogTitle>
          <DialogContent>
            <Autocomplete
              multiple
              options={availableFiles}
              getOptionLabel={(option) => option.filename}
              value={selectedFiles}
              onChange={(event, newValue) => setSelectedFiles(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Files"
                  placeholder="Choose files to add..."
                  margin="normal"
                  fullWidth
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.filename}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddFilesDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddFiles} 
              variant="contained"
              disabled={selectedFiles.length === 0}
            >
              Add {selectedFiles.length} File(s)
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Notes Dialog */}
        <Dialog open={editNotesDialogOpen} onClose={() => setEditNotesDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>
            {isMobile && (
              <IconButton
                onClick={() => setEditNotesDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            Edit File Notes & Tags
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Notes"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={editingNotes.notes}
              onChange={(e) => setEditingNotes({...editingNotes, notes: e.target.value})}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={editingNotes.tags}
              onChange={(event, newValue) => setEditingNotes({...editingNotes, tags: newValue})}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tags..."
                  variant="outlined"
                  helperText="Press Enter to add tags"
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditNotesDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateFileNotes} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create/Edit Checklist Item Dialog */}
        <Dialog open={checklistDialogOpen} onClose={() => setChecklistDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>
            {isMobile && (
              <IconButton
                onClick={() => setChecklistDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            {editingChecklistItem ? 'Edit Checklist Item' : 'Add Checklist Item'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Title *"
              fullWidth
              variant="outlined"
              value={editingChecklistItem?.title || newChecklistItem.title}
              onChange={(e) => {
                const value = e.target.value;
                if (editingChecklistItem) {
                  setEditingChecklistItem({...editingChecklistItem, title: value});
                } else {
                  setNewChecklistItem({...newChecklistItem, title: value});
                }
              }}
              sx={{ mb: 2 }}
              error={editingChecklistItem ? !editingChecklistItem.title?.trim() : !newChecklistItem.title?.trim()}
              helperText={editingChecklistItem ? (!editingChecklistItem.title?.trim() && "Title is required") : (!newChecklistItem.title?.trim() && "Title is required")}
            />
            
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={editingChecklistItem?.description || newChecklistItem.description}
              onChange={(e) => {
                const value = e.target.value;
                if (editingChecklistItem) {
                  setEditingChecklistItem({...editingChecklistItem, description: value});
                } else {
                  setNewChecklistItem({...newChecklistItem, description: value});
                }
              }}
              sx={{ mb: 2 }}
            />

            <Autocomplete
              multiple
              options={fileTypeOptions}
              value={editingChecklistItem?.expected_file_types || newChecklistItem.expected_file_types || []}
              onChange={(event, newValue) => {
                if (editingChecklistItem) {
                  setEditingChecklistItem({...editingChecklistItem, expected_file_types: newValue});
                } else {
                  setNewChecklistItem({...newChecklistItem, expected_file_types: newValue});
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option.toUpperCase()} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Expected File Types"
                  placeholder="Select file types..."
                  helperText="Leave empty to accept any file type"
                />
              )}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="dense"
              label="Notes"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              value={editingChecklistItem?.notes || newChecklistItem.notes}
              onChange={(e) => {
                const value = e.target.value;
                if (editingChecklistItem) {
                  setEditingChecklistItem({...editingChecklistItem, notes: value});
                } else {
                  setNewChecklistItem({...newChecklistItem, notes: value});
                }
              }}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editingChecklistItem?.required ?? newChecklistItem.required}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (editingChecklistItem) {
                      setEditingChecklistItem({...editingChecklistItem, required: checked});
                    } else {
                      setNewChecklistItem({...newChecklistItem, required: checked});
                    }
                  }}
                />
              }
              label="Required Item"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setChecklistDialogOpen(false);
              setEditingChecklistItem(null);
              setNewChecklistItem({ title: '', description: '', required: true, expected_file_types: [], notes: '' });
            }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (editingChecklistItem) {
                  handleUpdateChecklistItem();
                } else {
                  handleCreateChecklistItem();
                }
              }}
              variant="contained"
              disabled={editingChecklistItem ? !editingChecklistItem.title?.trim() : !newChecklistItem.title?.trim()}
            >
              {editingChecklistItem ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Fulfill Item Dialog */}
        <Dialog open={fulfillDialogOpen} onClose={() => setFulfillDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>
            {isMobile && (
              <IconButton
                onClick={() => setFulfillDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            Fulfill: {selectedChecklistItem?.title}
          </DialogTitle>
          <DialogContent>
            {selectedChecklistItem?.expected_file_types?.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Expected file types: {selectedChecklistItem.expected_file_types.join(', ').toUpperCase()}
              </Alert>
            )}
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Choose how to fulfill this item:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={uploadMode === 'upload' ? 'contained' : 'outlined'}
                  onClick={() => setUploadMode('upload')}
                  startIcon={<CloudUploadIcon />}
                  size="small"
                >
                  Upload New File
                </Button>
                <Button
                  variant={uploadMode === 'select' ? 'contained' : 'outlined'}
                  onClick={() => setUploadMode('select')}
                  startIcon={<AssignmentIcon />}
                  size="small"
                >
                  Select Existing File
                </Button>
              </Box>
            </Box>

            {uploadMode === 'upload' ? (
              <Box>
                {uploading ? (
                  <Box sx={{ py: 2 }}>
                    <Typography gutterBottom>Uploading file...</Typography>
                    <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      {uploadProgress}% complete
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      border: 2,
                      borderColor: 'divider',
                      borderStyle: 'dashed',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => document.getElementById('checklist-file-input').click()}
                  >
                    <input
                      id="checklist-file-input"
                      type="file"
                      accept={selectedChecklistItem?.expected_file_types?.length > 0 
                        ? selectedChecklistItem.expected_file_types.map(type => `.${type}`).join(',')
                        : '*/*'
                      }
                      onChange={handleChecklistFileUpload}
                      style={{ display: 'none' }}
                    />
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      {isMobile ? 'Tap to upload file' : 'Click to upload file'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      File will be uploaded and automatically fulfill this checklist item
                    </Typography>
                    {selectedChecklistItem?.expected_file_types?.length > 0 && (
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Accepted types: {selectedChecklistItem.expected_file_types.join(', ').toUpperCase()}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Autocomplete
                options={availableFiles}
                getOptionLabel={(option) => option.filename || option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select File"
                    placeholder="Choose a file to fulfill this item..."
                  />
                )}
                onChange={(event, value) => {
                  if (value) {
                    handleFulfillChecklistItem(selectedChecklistItem.id, value.id);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <FilePresentIcon color="primary" />
                      <Box>
                        <Typography variant="body2">{option.filename || option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.file_type && `${option.file_type} • `}
                          {option.uploaded_at && new Date(option.uploaded_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                )}
              />
            )}
          </DialogContent>
          {!isMobile && (
            <DialogActions>
              <Button onClick={() => {
                setFulfillDialogOpen(false);
                setUploadMode('select');
              }}>
                Cancel
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* Confirm Delete Dialog */}
        <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)} fullScreen={isMobile}>
          <DialogTitle>
            {isMobile && (
              <IconButton
                onClick={() => setConfirmDeleteDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            Delete Organizer
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{organizer.name}"? 
              This will remove the organizer but not the files themselves.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleDeleteOrganizer} 
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* File Preview Dialog */}
        <Dialog 
          open={filePreviewDialog.open} 
          onClose={() => setFilePreviewDialog({ open: false, file: null })}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {filePreviewDialog.file && getFileIcon(filePreviewDialog.file)}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6">{filePreviewDialog.file?.filename}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {filePreviewDialog.file?.file_type} • {filePreviewDialog.file && new Date(filePreviewDialog.file.added_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton onClick={() => handleViewFile(filePreviewDialog.file?.id)}>
                  <LaunchIcon />
                </IconButton>
                <IconButton onClick={() => handleDownloadFile(filePreviewDialog.file)}>
                  <DownloadIcon />
                </IconButton>
                <IconButton onClick={() => setFilePreviewDialog({ open: false, file: null })}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {filePreviewDialog.file && (
              <Box sx={{ textAlign: 'center' }}>
                {isImageFile(filePreviewDialog.file.filename) ? (
                  <img
                    src={filePreviewDialog.file.file_url}
                    alt={filePreviewDialog.file.filename}
                    style={{
                      maxWidth: '100%',
                      maxHeight: isMobile ? '60vh' : '70vh',
                      borderRadius: 4
                    }}
                  />
                ) : isPdfFile(filePreviewDialog.file.filename) ? (
                  <Box sx={{ height: isMobile ? 400 : 500 }}>
                    <embed
                      src={filePreviewDialog.file.file_url}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                    />
                  </Box>
                ) : (
                  <Box sx={{ py: 4 }}>
                    {getFileIcon(filePreviewDialog.file)}
                    <Typography variant="h6" sx={{ mt: 2 }}>
                      {filePreviewDialog.file.filename}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Preview not available for this file type
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<LaunchIcon />}
                      onClick={() => handleViewFile(filePreviewDialog.file.id)}
                      sx={{ mt: 2 }}
                    >
                      Open File
                    </Button>
                  </Box>
                )}
                
                {filePreviewDialog.file.notes && (
                  <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                    <Typography variant="body2">
                      <strong>Notes:</strong> {filePreviewDialog.file.notes}
                    </Typography>
                  </Alert>
                )}
                
                {filePreviewDialog.file.tags && filePreviewDialog.file.tags.length > 0 && (
                  <Box sx={{ mt: 2, textAlign: 'left' }}>
                    <Typography variant="caption" color="text.secondary">Tags:</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {filePreviewDialog.file.tags.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* File Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => {
            handleFilePreview(menuFile);
            setAnchorEl(null);
          }}>
            <VisibilityIcon sx={{ mr: 1 }} />
            Preview
          </MenuItem>
          <MenuItem onClick={() => {
            handleViewFile(menuFile?.id);
            setAnchorEl(null);
          }}>
            <LaunchIcon sx={{ mr: 1 }} />
            Open
          </MenuItem>
          <MenuItem onClick={() => {
            handleDownloadFile(menuFile);
            setAnchorEl(null);
          }}>
            <DownloadIcon sx={{ mr: 1 }} />
            Download
          </MenuItem>
          <MenuItem onClick={() => openEditNotesDialog(menuFile)}>
            <NotesIcon sx={{ mr: 1 }} />
            Edit Notes & Tags
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={() => {
              handleRemoveFile(menuFile.id);
              setAnchorEl(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} />
            Remove from Organizer
          </MenuItem>
        </Menu>

        {/* Checklist Context Menu */}
        <Menu 
          anchorEl={checklistMenuEl} 
          open={Boolean(checklistMenuEl)} 
          onClose={() => setChecklistMenuEl(null)}
        >
          <MenuItem onClick={() => {
            setEditingChecklistItem(checklistMenuItem);
            setChecklistDialogOpen(true);
            setChecklistMenuEl(null);
          }}>
            <EditIcon sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          {!checklistMenuItem?.completed && (
            <MenuItem onClick={() => {
              setSelectedChecklistItem(checklistMenuItem);
              setUploadMode('upload');
              setFulfillDialogOpen(true);
              setChecklistMenuEl(null);
            }}>
              <CloudUploadIcon sx={{ mr: 1 }} />
              Upload File
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={() => {
            handleDeleteChecklistItem(checklistMenuItem?.id);
            setChecklistMenuEl(null);
          }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* View Shares Dialog */}
        <Dialog 
          open={viewSharesDialogOpen} 
          onClose={() => setViewSharesDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton
                onClick={() => setViewSharesDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            <GroupIcon />
            Sharing "{organizer.name}"
          </DialogTitle>
          <DialogContent>
            {organizer.shares && organizer.shares.length > 0 ? (
              <Stack spacing={2}>
                {organizer.shares.map((share) => (
                  <Paper key={share.id} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                        <PersonIcon />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {share.shared_with_email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Shared {new Date(share.created_at).toLocaleDateString()} • {share.permission_level} access
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveShare(share.id)}
                        startIcon={<DeleteIcon />}
                      >
                        Remove
                      </Button>
                    </Box>
                    {share.share_message && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        "{share.share_message}"
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <GroupIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  This organizer is not shared with anyone yet.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewSharesDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setViewSharesDialogOpen(false);
                setShareMode('users');
                setShareDialogOpen(true);
              }}
              variant="contained"
              startIcon={<PersonAddIcon />}
            >
              Add People
            </Button>
          </DialogActions>
        </Dialog>

        {/* Organizer Chat Modal */}
        <OrganizerChatModal
          open={chatModalOpen}
          onClose={() => setChatModalOpen(false)}
          organizer={organizer}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({...snackbar, open: false})}
          anchorOrigin={{ 
            vertical: isMobile ? 'top' : 'bottom', 
            horizontal: 'center' 
          }}
        >
          <Alert 
            onClose={() => setSnackbar({...snackbar, open: false})} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default OrganizerDetailPage;